import { HTMLAttributes, ReactElement } from 'react'
import {
  DisplayProps,
  OverflowProps,
  SpaceProps,
  TextColorProps,
  TypographyProps,
} from 'styled-system'

import {
  TestIdProps,
  TextDecorationProps,
  TextOverflowProps,
  WhiteSpaceProps,
  WordBreakProps,
} from 'designSystem/types/propTypes'

import INTERNALxParagraph from 'designSystem/components/internal/elements/INTERNALxParagraph'
import INTERNALxSpan from 'designSystem/components/internal/elements/INTERNALxSpan'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { FONT_WEIGHT } from 'designSystem/foundations/constants/fontWeight'
import { LINE_HEIGHT } from 'designSystem/foundations/constants/lineHeight'

export type TextSize = 'sm' | 'md' | 'lg'
type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold'
type TextVariant = 'p' | 'span'

type BaseProps = HTMLAttributes<HTMLDivElement> &
  DisplayProps &
  OverflowProps &
  SpaceProps &
  TestIdProps &
  TextColorProps &
  TextDecorationProps &
  TextOverflowProps &
  TypographyProps &
  WordBreakProps &
  WhiteSpaceProps

type Props = {
  hideOverflow?: boolean
  size?: TextSize
  weight?: TextWeight
  variant?: TextVariant
} & BaseProps

export default function PDSText({
  hideOverflow = false,
  size = 'md',
  testId,
  variant,
  weight = 'regular',
  ...props
}: Props): ReactElement {
  let TextComponent: React.FunctionComponent = INTERNALxSpan

  switch (variant) {
    case 'p':
      TextComponent = INTERNALxParagraph
      break
    case 'span':
      TextComponent = INTERNALxSpan
      break
  }

  const overflowStyles = hideOverflow ? OVERFLOW_STYLES : null
  const sizeStyles = TEXT_SIZE[size] ?? TEXT_SIZE.md
  const weightStyles = TEXT_WEIGHT[weight] ?? TEXT_WEIGHT.regular

  return (
    <TextComponent
      data-testid={testId}
      fontFamily="Inter"
      {...sizeStyles}
      {...overflowStyles}
      {...weightStyles}
      {...props}
    />
  )
}

const OVERFLOW_STYLES = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const TEXT_SIZE = {
  sm: {
    fontSize: FONT_SIZE.fs12,
    lineHeight: LINE_HEIGHT.lh16,
  },
  md: {
    fontSize: FONT_SIZE.fs14,
    lineHeight: LINE_HEIGHT.lh20,
  },
  lg: {
    fontSize: FONT_SIZE.fs16,
    lineHeight: LINE_HEIGHT.lh24,
  },
}

const TEXT_WEIGHT = {
  regular: {
    fontWeight: FONT_WEIGHT.regular,
  },
  medium: {
    fontWeight: FONT_WEIGHT.medium,
  },
  semibold: {
    fontWeight: FONT_WEIGHT.semibold,
  },
  bold: {
    fontWeight: FONT_WEIGHT.bold,
  },
}
